import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useMarkdown } from '../../hooks/useMarkdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeMathjax from 'rehype-mathjax';
import rehypeRaw from 'rehype-raw';
import { PluggableList } from 'unified';
import 'katex/dist/katex.min.css';
import './Preview.css';
import ExportTools from '../Tools/ExportTools';

const Preview: React.FC = () => {
    // 使用hook获取markdown内容和渲染引擎设置
    const { 
        renderedMarkdown, 
        renderEngine, 
        switchRenderEngine 
    } = useMarkdown();

    // 添加调试信息
    const contentLength = renderedMarkdown ? renderedMarkdown.length : 0;
    const previewContent = renderedMarkdown || '无内容可预览';

    // 控制是否显示MathJax加载状态
    const [loadingMathJax, setLoadingMathJax] = useState<boolean>(false);

    // 当切换到MathJax时，可能需要等待其加载
    useEffect(() => {
        if (renderEngine === 'mathjax' && renderedMarkdown) {
            setLoadingMathJax(true);
            // MathJax加载通常很快，但我们仍然设置一个短暂的加载状态
            const timer = setTimeout(() => {
                setLoadingMathJax(false);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [renderEngine, renderedMarkdown]);

    // KaTeX配置选项
    const katexOptions = {
        // 输出为HTML和MathML（提高可访问性）
        output: 'htmlAndMathml',
        // 允许在\text{}中使用更多中文和特殊字符
        trust: true,
        // 错误时显示红色
        errorColor: '#cc0000',
        // 允许加载更多宏
        macros: {
            "\\R": "\\mathbb{R}"
        },
        // 添加更多字体支持
        throwOnError: false,
        strict: false
    };

    // 下载转换后的Markdown文件
    const handleDownload = () => {
        if (!renderedMarkdown) return;
        
        // 创建Blob对象
        const blob = new Blob([renderedMarkdown], { type: 'text/markdown;charset=utf-8' });
        
        // 创建下载链接
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'converted_markdown.md';
        
        // 触发下载
        document.body.appendChild(a);
        a.click();
        
        // 清理
        setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }, 0);
    };

    // 根据当前选择的渲染引擎获取rehype插件
    // 修复类型问题，确保返回类型与ReactMarkdown组件期望的类型兼容
    const getRehypePlugins = (): PluggableList => {
        if (renderEngine === 'katex') {
            return [[rehypeKatex, katexOptions], rehypeRaw];
        } else {
            return [rehypeMathjax, rehypeRaw];
        }
    };

    return (
        <div className="markdown-preview preview">
            <div className="preview-header">
                <div className="preview-left">
                    <h3>Rendered Output {contentLength > 0 ? `(${contentLength} 字符)` : ''}</h3>
                    
                    {/* 渲染引擎选择 */}
                    {renderedMarkdown && (
                        <div className="render-engine-selector">
                            <label>公式渲染引擎: </label>
                            <div className="engine-options">
                                <button 
                                    className={`engine-btn ${renderEngine === 'katex' ? 'active' : ''}`}
                                    onClick={() => switchRenderEngine('katex')}
                                    title="使用KaTeX渲染公式(更快，但对某些语法支持有限)"
                                >
                                    KaTeX
                                </button>
                                <button 
                                    className={`engine-btn ${renderEngine === 'mathjax' ? 'active' : ''}`}
                                    onClick={() => switchRenderEngine('mathjax')}
                                    title="使用MathJax渲染公式(更完整的语法支持，处理百分号更好)"
                                >
                                    MathJax
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                
                {renderedMarkdown && (
                    <button 
                        className="download-button" 
                        onClick={handleDownload}
                        title="下载转换后的Markdown文件"
                    >
                        下载MD文件
                    </button>
                )}
            </div>
            
            {/* 如果没有内容，显示提示 */}
            {!renderedMarkdown && (
                <div className="empty-content">
                    <p>点击"Convert and Render"按钮查看渲染结果</p>
                </div>
            )}
            
            {/* 加载MathJax时显示加载状态 */}
            {loadingMathJax && (
                <div className="loading-indicator">
                    <p>正在加载MathJax渲染引擎...</p>
                </div>
            )}
            
            {/* 渲染Markdown内容 */}
            <div className={`markdown-content ${loadingMathJax ? 'loading' : ''}`}>
                <ReactMarkdown 
                    remarkPlugins={[remarkGfm, remarkMath]}
                    rehypePlugins={getRehypePlugins()}
                >
                    {previewContent}
                </ReactMarkdown>
            </div>
            
            {/* 添加导出工具组件 */}
            {renderedMarkdown && <ExportTools className="preview-export-tools" />}
            
            {/* 添加原始内容调试显示，可在生产环境中移除 */}
            <details className="debug-info">
                <summary>调试信息</summary>
                <pre>{previewContent}</pre>
                <p>当前渲染引擎: {renderEngine}</p>
            </details>
        </div>
    );
};

export default Preview;